import type { FactoryEdit } from '@indutrust/factory-model';
import type { Country } from '@indutrust/shared-model';
import {
  ALL_VIEW_SECTION_IDS,
  FACTORY_VIEW_SECTION_ICONS,
  type FactoryViewSectionNavItem,
} from './factory-view-section.util';

/**
 * Deps object passed to {@link buildFactoryViewSectionNavItems} describing
 * the count of each sub-resource collection.
 */
export type FactoryViewSectionDeps = {
  documentsCount: number;
  machineParkCount: number;
  projectReferencesCount: number;
  tourNodesCount: number;
};

/**
 * Returns the factory when it passes the public-visibility gate
 * (published + verified, per `docs/product/05-roles-and-permissions.md` R14).
 * Returns `null` for any other state.
 *
 * @example
 * gateVisibleFactory(factory) // null when draft or unverified
 */
export function gateVisibleFactory(
  factory: FactoryEdit | null
): FactoryEdit | null {
  if (!factory) {
    return null;
  }
  if (factory.status !== 'published' || !factory.verified) {
    return null;
  }
  return factory;
}

/**
 * Returns `true` when the factory has at least one non-empty business field
 * to display in the Business section.
 *
 * @example
 * hasFactoryBusinessData(factory) // true when company_size > 0
 */
export function hasFactoryBusinessData(factory: FactoryEdit): boolean {
  const nonEmpty = (v: number | string | null | undefined): boolean =>
    v !== null && v !== undefined && v !== '' && v !== 0;
  return (
    !!factory.creation_date ||
    nonEmpty(factory.company_size) ||
    nonEmpty(factory.headcount_production) ||
    nonEmpty(factory.headcount_management) ||
    nonEmpty(factory.workshop_used) ||
    nonEmpty(factory.workshop_available) ||
    nonEmpty(factory.trade_license_number) ||
    nonEmpty(factory.turnover) ||
    factory.turnover_on_demand ||
    factory.shareholder_uae !== null ||
    factory.product_reference !== null ||
    factory.activity_reference !== null ||
    (factory.certification ?? []).length > 0
  );
}

/**
 * Compact organization fields for the map/project preview modal
 * (founded, size, workshop, certifications only).
 *
 * @example
 * hasFactoryPreviewOrganizationData(factory) // true when certifications exist
 */
export function hasFactoryPreviewOrganizationData(
  factory: FactoryEdit
): boolean {
  const nonEmpty = (v: number | string | null | undefined): boolean =>
    v !== null && v !== undefined && v !== '' && v !== 0;
  return (
    !!factory.creation_date ||
    nonEmpty(factory.company_size) ||
    nonEmpty(factory.workshop_used) ||
    nonEmpty(factory.workshop_available) ||
    (factory.certification ?? []).length > 0
  );
}

/**
 * Returns `true` when the factory has capability or industrial subtype data.
 *
 * @example
 * hasFactoryCapabilities(factory) // true when industrial_subtypes are present
 */
export function hasFactoryCapabilities(factory: FactoryEdit): boolean {
  return (
    factory.capability_ids.length > 0 ||
    factory.industrial_subtypes.length > 0 ||
    factory.capabilities.length > 0
  );
}

/**
 * Resolves the ISO alpha-2 country code for the factory's country.
 *
 * @example
 * resolveCountryCode(factory, countries) // "AE"
 */
export function resolveCountryCode(
  factory: FactoryEdit,
  countries: Country[]
): string | null {
  if (!factory.country_id) {
    return null;
  }
  return countries.find((c) => c.id === factory.country_id)?.code ?? null;
}

/**
 * Resolves the human-readable country name for the factory's country.
 *
 * @example
 * resolveCountryName(factory, countries) // "United Arab Emirates"
 */
export function resolveCountryName(
  factory: FactoryEdit,
  countries: Country[]
): string | null {
  if (!factory.country_id) {
    return null;
  }
  return countries.find((c) => c.id === factory.country_id)?.name ?? null;
}

/**
 * Builds the single-line location summary shown under the factory name in the
 * hero (industrial area + country name, joined by ", ").
 *
 * @example
 * buildLocationLine(factory, 'United Arab Emirates') // "Dubai Industrial City, United Arab Emirates"
 */
export function buildLocationLine(
  factory: FactoryEdit,
  countryName: string | null
): string | null {
  const parts: string[] = [];
  if (factory.industrial_area_name) {
    parts.push(factory.industrial_area_name);
  }
  if (countryName) {
    parts.push(countryName);
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Builds the section nav item list for the read-only factory view, including
 * only sections that have data to display.
 *
 * @example
 * buildFactoryViewSectionNavItems(factory, { documentsCount: 2, ... })
 */
export function buildFactoryViewSectionNavItems(
  factory: FactoryEdit,
  deps: FactoryViewSectionDeps
): FactoryViewSectionNavItem[] {
  const items: FactoryViewSectionNavItem[] = [];

  if (factory.description) {
    items.push({
      id: 'description',
      icon: FACTORY_VIEW_SECTION_ICONS.description,
    });
  }
  if (
    factory.country_id ||
    factory.industrial_area_name ||
    factory.address ||
    factory.po_box ||
    (factory.latitude && factory.longitude)
  ) {
    items.push({ id: 'location', icon: FACTORY_VIEW_SECTION_ICONS.location });
  }
  if (hasFactoryCapabilities(factory)) {
    items.push({
      id: 'capabilities',
      icon: FACTORY_VIEW_SECTION_ICONS.capabilities,
    });
  }
  if (hasFactoryBusinessData(factory)) {
    items.push({ id: 'business', icon: FACTORY_VIEW_SECTION_ICONS.business });
  }
  if (deps.machineParkCount > 0) {
    items.push({
      id: 'machinePark',
      icon: FACTORY_VIEW_SECTION_ICONS.machinePark,
    });
  }
  if (deps.projectReferencesCount > 0) {
    items.push({
      id: 'projectReferences',
      icon: FACTORY_VIEW_SECTION_ICONS.projectReferences,
    });
  }
  if (deps.documentsCount > 0) {
    items.push({ id: 'documents', icon: FACTORY_VIEW_SECTION_ICONS.documents });
  }
  if (factory.contacts.length > 0) {
    items.push({ id: 'contacts', icon: FACTORY_VIEW_SECTION_ICONS.contacts });
  }
  if (factory.images.length > 0) {
    items.push({ id: 'media', icon: FACTORY_VIEW_SECTION_ICONS.media });
  }
  if (deps.tourNodesCount > 0) {
    items.push({
      id: 'virtualTour',
      icon: FACTORY_VIEW_SECTION_ICONS.virtualTour,
    });
  }

  // Preserve the canonical section order
  return ALL_VIEW_SECTION_IDS.reduce<FactoryViewSectionNavItem[]>((acc, id) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      acc.push(item);
    }
    return acc;
  }, []);
}
